import { cn } from "@/lib/utils";
import { RiExternalLinkFill } from "react-icons/ri";

/**
 * Maps platform icon labels to their official brand color Tailwind classes.
 * Used to colorize platform icons consistently across the app.
 */

const BRAND_COLORS: Record<string, string> = {
  linkedin: "text-[#0A66C2]",
  x: "text-black dark:text-white",
  facebook: "text-[#1877F2]",
  youtube: "text-[#FF0000]",
  tiktok: "text-black dark:text-white",
  "google maps": "text-[#34A853]",
  indeed: "text-[#2164F3]",
  glassdoor: "text-[#0CAA41]",
  wttj: "text-[#FFCD00]",
};

const LOGO_DEV_TOKEN = "pk_ZOfbu3pASUCK_8vMzJIuVg";

/** Platforms whose icons are replaced by multicolor brand images. */
const PLATFORM_LOGO_URLS: Record<string, string> = {
  instagram: `https://img.logo.dev/instagram.com?token=${LOGO_DEV_TOKEN}&size=40`,
  google: `https://img.logo.dev/google.com?token=${LOGO_DEV_TOKEN}&size=40`,
  france_travail: `https://img.logo.dev/francetravail.fr?token=${LOGO_DEV_TOKEN}&size=40`,
  hellowork: `https://img.logo.dev/hellowork.com?token=${LOGO_DEV_TOKEN}&size=40`,
  pappers: "https://www.pappers.fr/img/favicon-96.png",
  "societe.com": "https://imgng.societe.com/img/apple-touch-icon-2023.png",
  comparably: `https://img.logo.dev/comparably.com?token=${LOGO_DEV_TOKEN}&size=40`,
  kununu: `https://img.logo.dev/kununu.com?token=${LOGO_DEV_TOKEN}&size=40`,
  great_place_to_work: `https://img.logo.dev/greatplacetowork.com?token=${LOGO_DEV_TOKEN}&size=40`,
  "google maps":
    "https://www.gstatic.com/images/branding/product/2x/maps_64dp.png",
};

/**
 * Returns the brand color Tailwind class for a given platform label.
 * Matches case-insensitively against known platform names.
 */
export function getPlatformBrandColor(label: string): string {
  const key = label.toLowerCase();

  // Platforms with a multicolor image logo don't need a text color
  if (PLATFORM_LOGO_URLS[key]) return "";

  // Direct match
  if (BRAND_COLORS[key]) return BRAND_COLORS[key];

  // Partial match (e.g. "LinkedIn Jobs" matches "linkedin")
  for (const [platform, color] of Object.entries(BRAND_COLORS)) {
    if (key.includes(platform)) return color;
  }

  return "";
}

/**
 * Returns a React icon component for a platform that uses a multicolor brand image.
 * Returns null if the platform doesn't have a registered image URL.
 */
export function getLogoPlatformIcon(
  label: string,
): React.ComponentType<{ className?: string }> | null {
  const key = label.toLowerCase();
  const url =
    PLATFORM_LOGO_URLS[key] ??
    Object.entries(PLATFORM_LOGO_URLS).find(([platform]) =>
      key.includes(platform),
    )?.[1];

  if (!url) return null;

  return ({ className }: { className?: string }) => (
    <img
      src={url}
      alt={label}
      className={cn("rounded-sm object-contain", className)}
    />
  );
}

/**
 * Returns a React icon component for a given review platform.
 * Falls back to an external-link icon for unrecognised platforms.
 */
export function getReviewPlatformIcon(
  platform: string,
): React.ComponentType<{ className?: string }> {
  const logoIcon = getLogoPlatformIcon(platform);
  if (logoIcon) return logoIcon;

  return RiExternalLinkFill;
}
